<template>
  <BaseDialog
    v-model="showTagListForm"
    :actionAllow="false"
    :title="`Add ${tripPlanningModule.tagPlural}`"
    cardTextClass="pa-0 ma-0"
    width="50vw"
  >
    <template #information>
      <v-row class="ma-0 pa-5">
        <v-col v-if="permissions && permissions.add">
          <SelectField
            :id="tagType == 'Vehicle' ? 'vehiclesTags' : 'tags'"
            :itemsList="itemList"
            itemText="name"
            itemValue="id"
            :item-disabled="isItemDisabled"
            :isClearable="true"
            :isSearchRequired="true"
            :label="
              tagType == 'Vehicle'
                ? `Select ${fleetModule.vehicle} ${tripPlanningModule.tag}`
                : `Select ${tripPlanningModule.tag}`
            "
            v-model="selectedTags"
          />
        </v-col>
        <v-col
          class="d-flex justify-start align-center"
          v-if="permissions && permissions.add"
        >
          <PrimaryButton
            id="tagListAdd"
            @click="submitTagForm"
            :disabled="!selectedTags"
            name="Add"
          >
          </PrimaryButton>
        </v-col>
        <v-col cols="12" class="pa-0 ma-0">
          <ListLayout
            :context="context"
            :gridStyle="{ height: '50vh', 'overflow-y': 'auto' }"
            :title="
              tagType == 'Vehicle'
                ? `${fleetModule.vehicle} ${tripPlanningModule.tagPlural}`
                : `${customerAddressModule.customerAddress} ${tripPlanningModule.tagPlural}`
            "
            :hasAfterTitle="false"
            :hasFilter="false"
            :isColumnShowVisibility="false"
            :tableData="tableData"
            :total="totalItems"
            :tableHeader="tableHeader"
            :noMargin="true"
          />
        </v-col>
      </v-row>
    </template>
  </BaseDialog>
</template>

<script>
import BaseDialog from "@/components/BaseComponents/BaseDialog.vue";
import ListLayout from "@/components/LayoutComponents/ListLayout.vue";
import { bus } from "@/main";
import TagDeleteButton from "@/components/Button/TagDeleteButton.vue";
import {
  tripPlanningModule,
  fleetModule,
  customerAddressModule,
} from "@/utils/moduleMapping";
import PaginationCount from "@/components//BaseComponents/PaginationCount.vue";

export default {
  components: {
    BaseDialog,
    ListLayout,
    TagDeleteButton,
    PaginationCount,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    parentObj: {
      type: Object,
    },
    titleKey: {
      type: String,
    },
    tagType: {
      type: String,
    },
  },
  data() {
    return {
      customerAddressModule,
      fleetModule,
      tripPlanningModule,
      permissions: null,
      totalItems: 0,
      vehicleHeader: [
        {
          headerName: "Tag",
          field: "tag",
          sortable: false,
          resizable: false,
        },
        {
          headerName: "Vehicle",
          field: "vehicle_number",
          sortable: false,
          resizable: false,
        },
        {
          headerName: "Actions",
          field: "actions",
          sortable: false,
          resizable: false,
          cellRenderer: "TagDeleteButton",
        },
      ],
      customerAddressHeader: [
        {
          headerName: "Tag",
          field: "tag",
          sortable: false,
          resizable: false,
        },
        {
          headerName: "Actions",
          field: "actions",
          sortable: false,
          resizable: false,
          cellRenderer: "TagDeleteButton",
        },
      ],
      tableData: [],

      selectedTags: null,
      itemList: [],
    };
  },
  computed: {
    showTagListForm: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    context() {
      return { parentComponent: this };
    },
    tableHeader() {
      if (this.tagType == "Vehicle") {
        return this.vehicleHeader;
      }
      if (this.tagType == "Customer Address") {
        return this.customerAddressHeader;
      }
    },
    assignedTags() {
      return this.tableData.map((item) => item.tag_id);
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.getTagTypeList();
      } else {
        this.selectedTags = null;
        this.$emit("getList");
      }
    },
  },
  methods: {
    isItemDisabled(item) {
      return this.assignedTags.indexOf(item.id) > -1;
    },
    deleteTag(id) {
      if (this.tagType == "Vehicle") {
        this.deleteVehicleTag(id);
      }
      if (this.tagType == "Customer Address") {
        this.deleteCustomerAddressTag(id);
      }
    },
    getTagTypeList() {
      if (this.tagType == "Vehicle") {
        this.getVehicleTagList();
      }
      if (this.tagType == "Customer Address") {
        this.getCustomerAddressTagList();
      }
      this.getTagList();
    },
    submitTagForm() {
      if (this.tagType == "Vehicle") {
        this.addTagForVehicle();
      }
      if (this.tagType == "Customer Address") {
        this.addTagForCustomerAddress();
      }
    },
    async getVehicleTagList() {
      bus.$emit("showLoader", true);
      await this.$api.vehicleTags
        .getVehicleTagsList({ vehicle: this.parentObj.id, limit: "all" })
        .then((res) => {
          this.tableData = res.data;
          this.totalItems = res.count;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    async addTagForVehicle() {
      bus.$emit("showLoader", true);
      let payload = {
        vehicle: this.parentObj.id,
        tag: this.selectedTags,
      };
      await this.$api.vehicleTags
        .createVehicleTagsObject(payload)
        .then(() => {
          this.selectedTags = null;
          this.getVehicleTagList();
          bus.$emit("showLoader", false);
          this.$emit("refresh");
        })
        .catch((err) => {
          if (err.data && err.data.non_field_errors) {
            err.data.non_field_errors.forEach((mes) => {
              bus.$emit("showToastMessage", {
                message: mes,
                color: "error",
              });
            });
            this.selectedTags = null;
          }
          bus.$emit("showLoader", false);
        });
    },
    async getCustomerAddressTagList() {
      bus.$emit("showLoader", true);
      await this.$api.customerAddressTags
        .getCustomerAddressTagsList({
          customer_address: this.parentObj.id,
          limit: "all",
        })
        .then((res) => {
          this.tableData = res.data;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    async addTagForCustomerAddress() {
      bus.$emit("showLoader", true);
      let payload = {
        customer_address: this.parentObj.id,
        tag: this.selectedTags,
      };
      await this.$api.customerAddressTags
        .createCustomerAddressTagsObject(payload)
        .then(() => {
          this.selectedTags = null;
          this.getCustomerAddressTagList();
          this.$emit("refresh");
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          if (err.data && err.data.non_field_errors) {
            err.data.non_field_errors.forEach((mes) => {
              bus.$emit("showToastMessage", {
                message: mes,
                color: "error",
              });
            });
            this.selectedTags = null;
          }
          bus.$emit("showLoader", false);
        });
    },
    async getTagList() {
      bus.$emit("showLoader", true);
      await this.$api.tag
        .getTagList({ limit: "all" })
        .then((res) => {
          this.itemList = res.data;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    deleteVehicleTag(id) {
      bus.$emit("showLoader", true);
      this.$api.vehicleTags
        .deleteVehicleTagsObject(id)
        .then(() => {
          this.getVehicleTagList();
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    deleteCustomerAddressTag(id) {
      bus.$emit("showLoader", true);
      this.$api.customerAddressTags
        .deleteCustomerAddressTagsObject(id)
        .then(() => {
          this.getCustomerAddressTagList();
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
  },
  created() {
    this.permissions = this.$permissions.getPermission("trip_planning");
  },
};
</script>

<style></style>
